import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type BuilderUiVersion = "v1" | "v2";

const DEFAULT_VERSION: BuilderUiVersion = "v1";
const STORAGE_KEY = "builder_ui_version";
const BUILDER_UI_VERSION_EVENT = "builder-ui-version-changed";

function readLocalVersion(): BuilderUiVersion {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw === "v2" ? "v2" : "v1";
}

function writeLocalVersion(version: BuilderUiVersion) {
  localStorage.setItem(STORAGE_KEY, version);
}

function notifyVersionChange(version: BuilderUiVersion) {
  writeLocalVersion(version);
  window.dispatchEvent(new CustomEvent<BuilderUiVersion>(BUILDER_UI_VERSION_EVENT, { detail: version }));
}

function isMissingColumnError(error: any): boolean {
  return error?.code === "42703" || /does not exist/i.test(String(error?.message || ""));
}

/**
 * Read & update the per-user Builder UI version preference (V1 = current builder,
 * V2 = new canvas). Persisted in user_settings.builder_ui_version (cross-device).
 */
export function useBuilderUiVersion() {
  const [version, setVersionState] = useState<BuilderUiVersion>(readLocalVersion());
  const [loading, setLoading] = useState(true);
  const supportsDbRef = useRef(true);

  useEffect(() => {
    const syncFromStorage = () => setVersionState(readLocalVersion());
    const handleStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) syncFromStorage();
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener(BUILDER_UI_VERSION_EVENT, syncFromStorage as EventListener);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(BUILDER_UI_VERSION_EVENT, syncFromStorage as EventListener);
    };
  }, []);

  const load = useCallback(async () => {
    try {
      if (!supportsDbRef.current) {
        setVersionState(readLocalVersion());
        setLoading(false);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("user_settings")
        .select("builder_ui_version")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        if (isMissingColumnError(error)) {
          supportsDbRef.current = false;
          setVersionState(readLocalVersion());
          setLoading(false);
          return;
        }
        console.error("Failed to load builder UI version:", error);
        setLoading(false);
        return;
      }

      const stored = (data?.builder_ui_version as BuilderUiVersion) ?? DEFAULT_VERSION;
      const resolved = stored === "v2" ? "v2" : "v1";
      setVersionState(resolved);
      notifyVersionChange(resolved);
    } catch (err) {
      console.error("Builder UI version error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setVersion = useCallback(async (next: BuilderUiVersion) => {
    const prev = version;
    setVersionState(next);
    notifyVersionChange(next);

    if (!supportsDbRef.current) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return;
    }

    const { error } = await supabase
      .from("user_settings")
      .upsert({
        user_id: user.id,
        builder_ui_version: next,
        updated_at: new Date().toISOString(),
      }, { onConflict: "user_id" });

    if (error) {
      if (isMissingColumnError(error)) {
        // Column doesn't exist in this deployment; localStorage is source of truth.
        supportsDbRef.current = false;
        return;
      }
      console.error("Failed to update builder UI version:", error);
      toast.error("Failed to update Builder UI version");
      setVersionState(prev);
      notifyVersionChange(prev);
    }
  }, [version]);

  return { version, setVersion, loading };
}
